/**
 * ProtectedWebStorage — referans-parite (features/platform/state/ProtectedWebStorage.ts).
 *
 * Tarayıcı depolama arka uçlarına (localStorage/sessionStorage/indexedDB/caches)
 * modül yükleme anında yakalanan güvenli referanslar sağlar. Prod'da
 * `installBrowserStorageAccessProtection` ile `window` üzerindeki erişimciler
 * silinir — böylece 3. parti script'ler depolamaya doğrudan erişemez, uygulama
 * ise yakalanmış referanslar üzerinden çalışmaya devam eder.
 *
 * FARK (bilinçli): kaynak-referans `import.meta.env.PROD` (Vite) kullanır; TEBHOS rspack
 * konvansiyonu `process.env.NODE_ENV` kullanılır (bkz. platform/types/Env).
 *
 * FARK (bilinçli): kaynak-referans bu modül import edilir edilmez korumayı otomatik
 * kurar. TEBHOS'ta zustand `persist` ve mevcut store'lar `window.localStorage`'ı
 * DOĞRUDAN kullandığı için otomatik kurulum onları kırardı — koruma bu yüzden
 * OPT-IN'dir: uygulama tüm depolama erişimini bu modüle taşıdıktan sonra
 * `installBrowserStorageAccessProtection()` açıkça çağrılmalıdır.
 */

function captureStorageBackend<T>(readStorage: () => T): T | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return readStorage();
  } catch {
    return null;
  }
}

const protectedLocalStorage = captureStorageBackend(() => window.localStorage);
const protectedSessionStorage = captureStorageBackend(() => window.sessionStorage);
const protectedIndexedDB = captureStorageBackend(() => window.indexedDB);
const protectedCacheStorage = captureStorageBackend(() => window.caches);

let browserStorageAccessProtected = false;

function getProtectedOrCurrentBackend<T>(capturedBackend: T | null, readStorage: () => T): T | null {
  if (capturedBackend || browserStorageAccessProtected) {
    return capturedBackend;
  }
  return captureStorageBackend(readStorage);
}

export function getProtectedLocalStorage(): Storage | null {
  return getProtectedOrCurrentBackend(protectedLocalStorage, () => window.localStorage);
}

export function getProtectedSessionStorage(): Storage | null {
  return getProtectedOrCurrentBackend(protectedSessionStorage, () => window.sessionStorage);
}

export function getProtectedIndexedDB(): IDBFactory | null {
  return getProtectedOrCurrentBackend(protectedIndexedDB, () => window.indexedDB);
}

export function getProtectedCacheStorage(): CacheStorage | null {
  return getProtectedOrCurrentBackend(protectedCacheStorage, () => window.caches);
}

function deleteWindowStorageProperty(property: "localStorage" | "sessionStorage" | "indexedDB" | "caches"): void {
  try {
    delete (window as Partial<Window>)[property];
  } catch {}
}

/** Prod'da window üzerindeki depolama erişimcilerini siler (bir kez çalışır). */
export function installBrowserStorageAccessProtection(): void {
  if (browserStorageAccessProtected || process.env.NODE_ENV !== "production" || typeof window === "undefined") {
    return;
  }
  browserStorageAccessProtected = true;
  deleteWindowStorageProperty("localStorage");
  deleteWindowStorageProperty("sessionStorage");
  deleteWindowStorageProperty("indexedDB");
  deleteWindowStorageProperty("caches");
}

export const installLocalStorageAccessProtection = installBrowserStorageAccessProtection;
